/**
 * Quick Buy Product Renderer
 * Handles product row rendering from GraphQL data
 */

class QuickBuyProductRenderer {
	constructor(tableBody) {
		this.tableBody = tableBody;
		this.templateId = 'ProductRowTemplate';
	}

	/**
	 * Format money from GraphQL
	 */
	formatMoney(amountStr, currencyCode = 'EUR') {
		const amount = parseFloat(amountStr);
		const price = new Intl.NumberFormat('de-DE', {
			style: 'currency',
			currency: currencyCode,
			minimumFractionDigits: 2,
			maximumFractionDigits: 2
		}).format(amount)
		let cleaned = price.replace(/\s/g, '');
		let symbol = cleaned.match(/[^\d.,]+$/)?.[0] || '';
		let amountValue = cleaned.replace(/[^\d.,]/g, '');
		let result = `${symbol}${amountValue}`;

		return result;
	}

	/**
	 * Build product row from template and map data
	 */
	buildProductRow(product) {
		const variant = product.variants.edges[0]?.node;
		if (!variant) return null;

		// Get template
		const template = document.getElementById(this.templateId);
		if (!template) {
			return null;
		}

		// Clone template content
		const row = template.content.cloneNode(true).querySelector('tr');

		// Extract product data
		const productId = product.id?.split('/').pop();
		const variantId = variant.id?.split('/').pop();
		const price = variant.price ? this.formatMoney(variant.price.amount, variant.price.currencyCode) : '€0.00';
		const compareAtPrice = variant.compareAtPrice ? this.formatMoney(variant.compareAtPrice.amount, variant.compareAtPrice.currencyCode) : null;
		const hasNew = product.tags.includes('new_item') || product.tags.includes('new');
		const quantity = variant.quantityAvailable || 0;
		const barcode = variant.barcode || '—';
		const imageUrl = product.featuredImage?.url || '';

		// Map data to row
		row.setAttribute('data-compare-at-price', compareAtPrice || '');
		row.setAttribute('data-net-price', price);
		row.setAttribute('data-product-handle', product.handle);
		row.setAttribute('data-available-qty', String(quantity));
		if (variantId) row.setAttribute('data-variant-id', variantId);

		// NEW badge
		const newBadgeContainer = row.querySelector('.qb-new-badge-container');
		if (hasNew) {
			newBadgeContainer.innerHTML = '<p class="qb-product-name-badge-new">New</p>';
		}

		// PROMO badge (discount percentage)
		const promoBadgeContainer = row.querySelector('.qb-promo-badge-container');
		if (variant.compareAtPrice && variant.price) {
			promoBadgeContainer.innerHTML = `<p class="qb-product-name-badge-promo">%</p>`;
		}

		// Product image
		const imageContainer = row.querySelector('.qb-image-hover-container');
		imageContainer.setAttribute('data-image-url', imageUrl);

		const thumbImage = row.querySelector('.qb-product-image');
		thumbImage.src = imageUrl;
		thumbImage.alt = '';

		const hoverImage = row.querySelector('.qb-hover-full-image');
		hoverImage.src = imageUrl;
		hoverImage.alt = '';

		// Product name
		row.querySelector('.qb-product-name').textContent = product.title;

		// Barcode
		row.querySelector('.qb-product-barcode').textContent = barcode;

		// Stock info
		const stockContainer = row.querySelector('.qb-stock-container');
		if (quantity > 0 && quantity < 6) {
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-low-stock">${quantity} </p>`;
		} else if (quantity >= 6) {
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-stock">${quantity}</p>`;
		} else {
			stockContainer.innerHTML = `<p class="stock-info-wrapper stock-info-sold-out">Sold out</p>`;
		}

		// Promo price
		const promoPriceContainer = row.querySelector('.qb-promo-price-container');
		if (compareAtPrice) {
			promoPriceContainer.innerHTML = `<p class="compare-at-price-value">${price}</p>`;
		}

		// Net price
		const netPriceElement = row.querySelector('.qb-product-net-price');
		if (compareAtPrice) {
			netPriceElement.textContent = compareAtPrice;
			netPriceElement.style.textDecoration = 'line-through';
		} else {
			netPriceElement.textContent = price;
		}

		// Create and append purchase controls (inline only – no Spark product card)
		const sparkContainer = row.querySelector('.qb-spark-container');
		if (sparkContainer) {
			const purchaseWrap = document.createElement('div');
			purchaseWrap.className = 'qb-inline-purchase';
			purchaseWrap.innerHTML = `
				<div class="qb-quantity-selector">
					<button type="button" class="qb-quantity-btn" data-action="decrement" aria-label="Decrease quantity">-</button>
					<input class="qb-quantity-input" type="number" min="1" value="1" inputmode="numeric" pattern="[0-9]*" data-available-qty="${quantity}" ${quantity > 0 ? `max="${quantity}"` : ''} />
					<button type="button" class="qb-quantity-btn" data-action="increment" aria-label="Increase quantity">+</button>
				</div>
				<button type="button" class="qb-add-to-cart-btn" data-variant-id="${variantId || ''}">Add to cart</button>
				<div class="qb-quantity-message" role="status" aria-live="polite"></div>
			`;

			const qtyInput = purchaseWrap.querySelector('.qb-quantity-input');
			const qtyButtons = purchaseWrap.querySelectorAll('.qb-quantity-btn');
			const addToCartBtn = purchaseWrap.querySelector('.qb-add-to-cart-btn');

			if (quantity > 0) {
				qtyInput.max = String(quantity);
			} else {
				qtyInput.disabled = true;
				qtyButtons.forEach(btn => { btn.disabled = true; });
				addToCartBtn.disabled = true;
			}

			if (!variantId) {
				addToCartBtn.disabled = true;
			}

			sparkContainer.appendChild(purchaseWrap);
		}

		return row;
	}

	/**
	 * Render multiple products
	 */
	renderProducts(products) {
		if (!this.tableBody) {
			return;
		}

		// Clear tbody
		this.tableBody.innerHTML = '';

		if (products.length > 0) {
			// Create document fragment for better performance
			const fragment = document.createDocumentFragment();

			let successCount = 0;
			products.forEach((product, index) => {
				try {
					const row = this.buildProductRow(product);
					if (row) {
						fragment.appendChild(row);
						successCount++;
					}
				} catch (error) {
				}
			});

			// Append all rows at once
			this.tableBody.appendChild(fragment);

			// Note: Discount will be auto-applied by MutationObserver
			// No need to manually call reapplyActiveDiscount here
		} else {
			this.tableBody.innerHTML = '<tr><td colspan="8"><p>No products found</p></td></tr>';
		}
	}

	/**
	 * No-op for compatibility: Spark product card removed; inline UI only.
	 */
	waitForShadowDOMAndInitialize(onReady) {
		if (onReady && typeof onReady === 'function') onReady();
	}
}

// Export for use in other scripts
window.QuickBuyProductRenderer = QuickBuyProductRenderer;
